import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid } from '@mui/material'
import axios from 'axios'
import MDButton from 'components/MDButton'
import { UserDataContext } from 'context'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ReactToPrint from 'react-to-print'
import FooterTailwind from 'TailwindcssComponents/Footer'
import TailwindLayout from 'TailwindcssComponents/TailwindLayout'
import TopBar from 'TailwindcssComponents/TopBar'
import QRCode from 'qrcode';
import defaultcover from 'assets/images/logos/defaultcover.png'
import defaultcoverWhite from 'assets/images/logos/defaultCoverWhite.png'

export default function BookedTicketView() {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const componentRef = useRef();
    const location = useLocation();
    const navigate = useNavigate();
    const { registrationId, eventId } = location.state || {};
    const [isLoading, setIsLoading] = useState(false);
    const [ticketData, setTicketData] = useState(null);
    const [open, setOpen] = useState(false);

    const generateQRCodes = async (referenceId, registrationId, eventId, ticketId) => {
        try {
            const qrCodeDataUrl = await QRCode.toDataURL(String(`ReferenceID:${referenceId} && RegistrationID:${registrationId} && EventID:${eventId} && TicketID:${ticketId}`));
            return qrCodeDataUrl;
        } catch (err) {
            console.log('Error generating QR code:', err);
        }
    }

    const getTicketData = async () => {
        try {
            setIsLoading(true);
            if (!(eventId && registrationId)) {
                return null;
            }

            const [eventResponse, registrantResponse, ticketResponse, zonesResponse] = await Promise.all([
                axios.get(`${apiUrl}event-service/event/${eventId}`),
                axios.get(`${apiUrl}event-service/event-registration/${registrationId}`),
                axios.get(`${apiUrl}event-service/event-ticket`, { params: { registration: registrationId, event: eventId } }),
                axios.get(`${apiUrl}event-service/event-zone`, { params: { event: eventId } }),
            ]);

            if (!eventResponse.data?.success || !eventResponse.data?.data) {
                throw new Error('Failed to fetch event details');
            }
            if (!registrantResponse.data?.success || !registrantResponse.data?.data) {
                throw new Error('Failed to fetch event details');
            }
            if (!ticketResponse.data?.success || !ticketResponse.data?.data) {
                throw new Error('Failed to fetch ticket category details');
            }
            if (!zonesResponse.data?.success || !zonesResponse.data?.data) {
                throw new Error('Failed to fetch ticket category details');
            }

            // Extract event details
            const event = eventResponse.data.data;
            const registrations = registrantResponse.data.data;
            const tickets = ticketResponse?.data.data;
            const zones = zonesResponse.data.data;
            const enrichedTicket = {
                tickets: await Promise.all(
                    tickets.map(async (ticket) => {
                        const zone = zones.find(zone => zone.id === ticket?.ticketCategory?.zoneId) || {};
                        const seat = zone?.seats?.find(seat => seat.id === ticket.seatId) || null;
                        return {
                            ...ticket,
                            qrCode: await generateQRCodes(ticket.referenceId, registrationId, eventId, ticket.id),
                            zone,
                            seat,
                        };
                    })
                ),
                event,
                registrations,
            };
            setTicketData(enrichedTicket);
        } catch (error) {
            console.error('Error in fetching ticket data:', error.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getTicketData();
    }, [])

    const formattedDateTime = (date) => {
        return new Date(date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    };

    const formattedTime = (time) => {
        const [hours, minutes, seconds] = time.split(':');
        const date = new Date();
        date.setHours(hours, minutes, seconds);
        return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
    };

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        navigate(-1);
    };

    const waitForImagesToLoad = async (element) => {
        const images = element.getElementsByTagName('img');
        const promises = [];
        for (let img of images) {
            if (!img.complete) {
                promises.push(
                    new Promise((resolve) => {
                        img.onload = img.onerror = resolve;
                    })
                );
            }
        }
        return Promise.all(promises);
    };

    const handleDownloadAllTickets = async () => {
        const jsPDF = (await import('jspdf')).default;
        const html2canvas = (await import('html2canvas')).default;
        const { tickets, event, registrations } = ticketData;

        const a7Width = 210; // in points
        const a7Height = 370; // in points
        const doc = new jsPDF('p', 'pt', [a7Width, a7Height]);

        for (let index = 0; index < tickets.length; index++) {
            const ticket = tickets[index];
            const ticketElement = document.getElementById(`ticket-${ticket.id}`);
            await waitForImagesToLoad(ticketElement);

            const canvas = await html2canvas(ticketElement, { useCORS: true });
            const imgData = canvas.toDataURL('image/png');

            const imgWidth = a7Width;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            if (index !== 0) doc.addPage([a7Width, a7Height]);
            doc.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
        }

        doc.save(`${event?.name}_${registrations?.name}.pdf`);
    };

    const handleDownloadEachTicket = async () => {
        const jsPDF = (await import('jspdf')).default;
        const html2canvas = (await import('html2canvas')).default;
        const { tickets, event, registrations } = ticketData;

        const a7Width = 210; // in points
        const a7Height = 370; // in points

        for (let index = 0; index < tickets.length; index++) {
            const ticket = tickets[index];
            const ticketElement = document.getElementById(`ticket-${ticket.id}`);
            await waitForImagesToLoad(ticketElement);

            const canvas = await html2canvas(ticketElement, { useCORS: true });
            const imgData = canvas.toDataURL('image/png');

            const doc = new jsPDF('p', 'pt', [a7Width, a7Height]);
            const imgWidth = a7Width;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            doc.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
            doc.save(`${event?.name}_${registrations?.name}_${index + 1}.pdf`);
        }
    };

    return (
        <TailwindLayout>
            <TopBar />
            <div className="p-12 bg-gray-100 min-h-screen">
                {isLoading ? (
                    <div className="p-6 flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                ) : (
                    <>
                        {ticketData?.tickets && ticketData?.tickets.length > 0 && (
                            <div className="container mx-auto px-4 py-6">
                                <div className="mb-6 -mt-6">
                                    <h1 className="text-2xl font-bold">Your Tickets</h1>
                                    <p className="text-gray-600">Your booking for {ticketData.tickets.length} ticket(s) to {ticketData.event?.name || 'this'} has been confirmed.</p>
                                </div>

                                {/* Ticket Grid - Responsive */}
                                <div ref={componentRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
                                    {ticketData.tickets.map((ticket) => (
                                        <div key={ticket.id} id={`ticket-${ticket.id}`}>
                                            <div className="relative bg-white w-full overflow-hidden rounded-xl shadow-[0_-2px_10px_0_rgba(0,0,0,0.1)]">

                                                {/* Event Cover Image */}
                                                <div className="pt-4 px-4">
                                                    <div className="h-48 rounded-lg overflow-hidden">
                                                        <img
                                                            src={ticketData.event?.coverImage?.secure_url || defaultcover}
                                                            alt="Event Cover"
                                                            className="object-cover w-full h-full"
                                                            crossOrigin="anonymous"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="p-2 flex flex-col items-center">
                                                    {/* QR Code Section */}
                                                    <div className="bg-white -mt-20 rounded-lg mb-4 p-2 shadow-md">
                                                        <div className="w-36 h-36 bg-white flex items-center justify-center border border-gray-200">
                                                            <div className="text-center">
                                                                {ticket?.qrCode && (
                                                                    <img
                                                                        src={ticket.qrCode}
                                                                        alt="QR Code"
                                                                        className="h-32 w-32"
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>
                                                        <p className="text-xs text-center mt-1 text-gray-500">{ticket.referenceId}</p>
                                                    </div>

                                                    {/* Event Title */}
                                                    <h2 className="text-2xl font-bold text-center mb-1">{ticketData.event?.name || "N/A"}</h2>
                                                    <div className="flex items-center text-sm text-gray-600 mb-6">
                                                        <span className="text-lg mr-1">📍</span>
                                                        <span>{ticketData.event?.venue?.name || "N/A"}</span>
                                                    </div>

                                                    {/* Ticket Info Grid */}
                                                    <div className=" w-full flex justify-center">
                                                        <div className="grid grid-cols-3 w-full gap-4 mb-6">
                                                            <div className="flex flex-col items-center">
                                                                <div className="flex items-center text-gray-500 mb-1">
                                                                    <span className="text-lg mr-1">📅</span>
                                                                    <span className="text-xs">Date</span>
                                                                </div>
                                                                <p className="text-sm font-medium text-center">{formattedDateTime(ticketData?.event?.eventDateTimes[0]?.date)}</p>
                                                            </div>

                                                            <div className="flex flex-col items-center border-l-2 border-r-2 border-dashed">
                                                                <div className="flex items-center text-gray-500 mb-1">
                                                                    <span className="text-lg mr-1">👤</span>
                                                                    <span className="text-xs">Name</span>
                                                                </div>
                                                                <p className="text-sm font-medium text-center">{ticketData?.registrations?.name || 'N/A'}</p>
                                                            </div>

                                                            <div className="flex flex-col items-center">
                                                                <div className="flex items-center text-gray-500 mb-1">
                                                                    <span className="text-lg mr-1">🕞</span>
                                                                    <span className="text-xs">Time</span>
                                                                </div>
                                                                <p className="text-sm font-medium text-center">{formattedTime(ticketData?.event?.eventDateTimes[0]?.startTime)}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {ticket.seatId && (
                                                        <div className=" w-full flex justify-center">
                                                            <div className="grid grid-cols-3 w-full gap-4 mb-6">
                                                                <div className="flex flex-col items-center">
                                                                    <p className="text-xs text-gray-500">Zone</p>
                                                                    <p className="text-sm font-medium text-center">{ticket.zone?.name || "-"}</p>
                                                                </div>

                                                                <div className="flex flex-col items-center border-l-2 border-r-2 border-dashed">
                                                                    <p className="text-xs text-gray-500">Row</p>
                                                                    <p className="text-sm font-medium text-center">{ticket.seat?.row || "-"}</p>
                                                                </div>

                                                                <div className="flex flex-col items-center">
                                                                    <p className="text-xs text-gray-500">Seat</p>
                                                                    <p className="text-sm font-medium text-center">{ticket.seat?.name || "-"}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="w-full flex items-center">
                                                        <div className="flex-grow border-t-2 border-dashed mx-2"></div>
                                                    </div>
                                                    {/* footer */}
                                                    <div className="flex flex-col items-center pt-4 pb-3 w-full">
                                                        <div className="w-full flex justify-center">
                                                            <img
                                                                src={defaultcoverWhite}
                                                                alt="logo"
                                                                className="h-10"
                                                            />
                                                        </div>
                                                        <p className="text-sm font-medium text-gray-600 mt-2">
                                                            Scan your QR code at the entry gate.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                    ))}
                                </div>

                                <div className="flex gap-4 mt-4">
                                    <button
                                        onClick={handleDownloadAllTickets}
                                        className="px-6 py-3 bg-blue-500 text-white rounded-full shadow-lg hover:bg-blue-600"
                                    >
                                        Download All Tickets
                                    </button>
                                    {ticketData?.tickets?.length > 1 && (
                                        <button
                                            onClick={handleDownloadEachTicket}
                                            className="px-6 py-3 bg-blue-500 text-white rounded-full shadow-lg hover:bg-blue-600"
                                        >
                                            Download Each Ticket
                                        </button>
                                    )}
                                </div>

                            </div>
                        )}
                    </>
                )}
            </div>
            <FooterTailwind />
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Tickets Have Been SuccessFully Booked !
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Do you want to print tickets?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <ReactToPrint trigger={() => <MDButton disabled={ticketData?.tickets.length <= 0}>Yes</MDButton>} content={() => componentRef.current} onAfterPrint={handleClose} />
                    <MDButton onClick={handleClose}>No</MDButton>
                </DialogActions>
            </Dialog>

        </TailwindLayout>
    )
}
